/**
 * WinnerCard — Hall of Fame entry. Featured winners get gold treatment.
 */
import { Crown, Trophy, MapPin, Quote } from 'lucide-react';
import CatchImage from './CatchImage';

const TIER_BORDER = {
  basic: 'border-white/10',
  pro: 'border-[#c8b273]/40',
  elite: 'border-[#c8b273] shadow-[0_0_20px_rgba(200,178,115,0.15)]',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const WinnerCard = ({ winner: w }) => {
  const tier = w.competition_tier || 'basic';
  return (
    <div
      data-testid={`winner-card-${w.id}`}
      className={`glass-card overflow-hidden border-2 ${TIER_BORDER[tier] || TIER_BORDER.basic}`}
    >
      {/* Image */}
      <div className="relative h-48 bg-gradient-to-br from-[#1a1d1a] to-[#0f110f] overflow-hidden">
        <CatchImage row={w} aspectRatio="auto" className="w-full h-full" alt={w.title || 'Winning catch'} />
        <div className="absolute top-3 left-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#c8b273] shadow-lg">
            <Crown className="h-5 w-5 text-black" />
          </div>
        </div>
        {tier !== 'basic' && (
          <span className="absolute top-3 right-3 bg-[#c8b273] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
            {tier === 'elite' ? 'Elite Winner' : 'Pro Winner'}
          </span>
        )}
      </div>

      <div className="p-5">
        <p className="data-label text-[#c8b273] mb-2">{w.competition_category || 'Winner'}</p>
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-1">
          {w.competition_title || w.title}
        </h3>
        <p className="text-sm text-[#c8b273] font-bold mb-2">@{w.username}</p>

        {/* Catch stats */}
        <div className="flex items-center gap-3 text-xs text-[#8a948a] mb-3">
          {w.weight && (
            <span className="inline-flex items-center gap-1">
              <Trophy className="h-3.5 w-3.5" /> {w.weight} lb
            </span>
          )}
          {w.lake_name && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {w.lake_name}
            </span>
          )}
          <span>· {formatDate(w.awarded_at)}</span>
        </div>

        {/* Prize */}
        {w.prize && (
          <div className="text-sm text-[#e2e4e2] mb-3">
            <span className="text-[#8a948a]">Prize:</span> {w.prize}
          </div>
        )}

        {/* Quote */}
        {w.quote && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="flex gap-2">
              <Quote className="h-3.5 w-3.5 text-[#c8b273] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#c0c4c0] italic">"{w.quote}"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WinnerCard;
